import { memo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import { cn } from "../../lib/cn";
import { PROJECT_GRADIENTS } from "../../constants/gradients";
import { LongArrow } from "../../assets/icons";

const pickGradient = (id) => {
  const keys = Object.keys(PROJECT_GRADIENTS);
  return (
    PROJECT_GRADIENTS[keys[Math.abs(Number(id || 0)) % keys.length]] ||
    PROJECT_GRADIENTS["gradient-1"]
  );
};

const STORAGE_FULL_THRESHOLD = 80;

const TeamCard = memo(function TeamCard({ team, variants }) {
  const navigate = useNavigate();
  const gradientClass = pickGradient(team.id);
  const storageUsed = team.storage_used_bytes || 0;
  const storageQuota = Math.max(team.storage_quota_bytes || 1, 1);
  const storagePercent = Math.min(Math.round((storageUsed / storageQuota) * 100), 100);
  const showStorageWarning = storagePercent >= STORAGE_FULL_THRESHOLD;
  const memberCount = team.member_count ?? 0;
  const projectCount = team.project_count ?? 0;
  const membersPreview = team.members_preview || [];
  const typeLabel = team.is_personal ? "Personal workspace" : "Shared team";
  const description = team.description || (team.is_personal
    ? "Your default private workspace."
    : "Shared collaboration space.");

  return (
    <motion.div
      variants={variants}
      whileHover={{ y: -2 }}
      transition={{ type: "spring", stiffness: 260, damping: 24 }}
      className="h-full"
    >
      <Link
        to={`/teams/${team.id}`}
        aria-label={`Open ${team.name || "team"} workspace`}
        className="group block h-full overflow-hidden rounded-2xl border border-surface-border bg-white shadow-sm transition-all duration-200 hover:border-mediumgrey hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
      >
        <div className={cn("h-1.5 w-full bg-gradient-to-r", gradientClass)} />

        <div className="flex h-full flex-col p-5">
          <div className="space-y-3">
            <div className="space-y-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-mediumgrey">
                {typeLabel}
              </p>
              <h3 className="font-sans text-xl font-semibold leading-tight text-darkgrey line-clamp-2">
                {team.name}
              </h3>
            </div>

            <p className="text-sm leading-6 text-grey line-clamp-2">
              {description}
            </p>
          </div>

          <div className="mt-6 flex flex-1 flex-col justify-end gap-4 border-t border-surface-border pt-4">
            {showStorageWarning ? (
              <div className="flex items-center justify-between gap-3 rounded-xl bg-warning/10 px-3 py-2 text-xs text-warning">
                <span className="font-medium">Storage nearly full</span>
                <span>{storagePercent}% used</span>
              </div>
            ) : null}

            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-4">
                <div className="flex items-center">
                  {membersPreview.slice(0, 3).map((member, idx) => (
                    <div
                      key={member.id}
                      className={cn(
                        "h-7 w-7 rounded-full border border-white bg-surface-hover object-cover",
                        idx > 0 && "-ml-2"
                      )}
                      style={
                        member.profile_image_url
                          ? {
                              backgroundImage: `url(${member.profile_image_url})`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                            }
                          : undefined
                      }
                      title={member.name}
                    >
                      {!member.profile_image_url && (
                        <span className="flex h-full w-full items-center justify-center text-[10px] font-semibold text-darkgrey">
                          {member.name?.charAt(0)?.toUpperCase() || "?"}
                        </span>
                      )}
                    </div>
                  ))}
                  {memberCount > 3 && (
                    <div className="-ml-2 flex h-7 w-7 items-center justify-center rounded-full border border-white bg-surface-hover">
                      <span className="text-[10px] font-medium text-darkgrey">
                        +{memberCount - 3}
                      </span>
                    </div>
                  )}
                </div>

                <div className="min-w-0 text-sm text-mediumgrey">
                  {projectCount} {projectCount === 1 ? "project" : "projects"}
                </div>
              </div>

              <button
                type="button"
                aria-label={`Open ${team.name || "team"} workspace`}
                className="shrink-0 rounded-full p-2 text-darkgrey transition-transform duration-200 group-hover:translate-x-1"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  navigate(`/teams/${team.id}`);
                }}
              >
                <LongArrow className="fill-grey" />
              </button>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
});

export { TeamCard };
export default TeamCard;
